import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PaginatedSiteSearchResponse, ProductImportResponse, SiteSetup, SiteSetupResponse } from '../models/response.model';
import { PlantSetup, PlantSetupResponse, PlantZoneSettingsResponse, TdmsProperties } from '../models/plant-setup.model';
import { ZoneSetupResponse } from '../models/zone-setup.model';
import { EmailSetup, EmailSetupFiltersResponse, PaginatedEmailSetupResponse } from '../models/email-setup.model';

@Injectable({
  providedIn: 'root'
})
export class SetupScreensService {
  private http = inject(HttpClient);

  getAllSites(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedSiteSearchResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getPaginatedSites}`;
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedSiteSearchResponse>(url, { params });
  }

  getSiteDetails(siteId: string): Observable<SiteSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getSiteDetails}`;
    return this.http.get<SiteSetupResponse>(url, { params: { siteId } });
  }

  updateSite(payload: SiteSetup): Observable<SiteSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateSite}`;
    return this.http.post<SiteSetupResponse>(url, payload);
  }

  getAllPlants(): Observable<PlantSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllActiveInactivePlants}`;
    return this.http.get<PlantSetupResponse>(url);
  }

  getPlants(): Observable<PlantSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getPlants}`;
    return this.http.get<PlantSetupResponse>(url);
  }

  getPlantAndZoneSetting(plantId: string): Observable<PlantZoneSettingsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getPlantAndZoneSettings}`;
    return this.http.get<PlantZoneSettingsResponse>(url, { params: { plantId } });
  }

  getZoneSetting(): Observable<PlantZoneSettingsResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getZoneSettings}`;
    return this.http.get<PlantZoneSettingsResponse>(url);
  }

  updatePlant(payload: PlantSetup): Observable<PlantSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updatePlant || 'v1/plants/updatePlant'}`;
    return this.http.post<PlantSetupResponse>(url, payload);
  }

  createPlant(payload: any): Observable<PlantSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.createPlant || 'plants/createPlant'}`;
    return this.http.post<PlantSetupResponse>(url, payload);
  }

  importProducts(): Observable<ProductImportResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.importProducts}`;
    return this.http.get<ProductImportResponse>(url);
  }

  getNewTdmsId(): Observable<string> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getNewTdmsId}`;
    return this.http.get<string>(url);
  }

  updateTdmsProperties(payload: TdmsProperties): Observable<TdmsProperties> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateTdmsProperties}`;
    return this.http.put<TdmsProperties>(url, payload);
  }

  deleteTdmsProperties(tdmsId: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteTdmsProperties}/${tdmsId}`;
    return this.http.delete<void>(url);
  }

  deleteDealerPlantTransfer(plantTransferPk: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deletePlantTransfer}/${plantTransferPk}`;
    return this.http.delete<any>(url);
  }

  // Email Setup
  getEmailSetups(filters: any, pageNumber: number, pageSize: number): Observable<PaginatedEmailSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllEmailSetups}`;
    const filteredParams = Object.entries(filters || {}).reduce((acc, [key, value]) => {
      if (value != null && value !== '' && typeof value !== 'object') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    return this.http.get<PaginatedEmailSetupResponse>(url, { params });
  }

  getEmailSetupFilters(): Observable<EmailSetupFiltersResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllEmailSetupFilters}`;
    return this.http.get<EmailSetupFiltersResponse>(url);
  }

  updateEmailSetup(payload: EmailSetup[]): Observable<EmailSetup[]> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateEmailSetup}`;
    return this.http.post<EmailSetup[]>(url, payload);
  }

  deleteEmailSetup(emailPk: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.deleteEmailSetup}`;
    return this.http.post<void>(url, emailPk);
  }

  getNewEmailSetupPk(): Observable<string> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getNewEmailSetupPk}`;
    return this.http.get<string>(url);
  }

  // Location dropdown APIs for Email Setup
  getAllSitesForDropdown(): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllSitesForDropdown}`;
    return this.http.get<any>(url);
  }

  getAllCustomersForDropdown(): Observable<any> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getAllCustomersForDropdown}`;
    return this.http.get<any>(url);
  }

  getZoneDetails(): Observable<ZoneSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.getZoneDetails}`;
    return this.http.get<ZoneSetupResponse>(url);
  }

  updateZoneSetup(payload: any): Observable<ZoneSetupResponse> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.updateZoneSetup}`;
    return this.http.post<ZoneSetupResponse>(url, payload);
  }
}
